import AsyncStorage from "@react-native-async-storage/async-storage";
import { Buffer } from "buffer";
import deepmerge from "deepmerge";
import zlib from "react-zlib-js";
import { promisify } from "util";
import { clone } from "../utils/CoreUtils";
import { getActionByName } from "./actions/Actions";
import { clearFlag, hasGlobalFlagIncludingNextRun } from "./Flags";
import { PlayerContextState, PLAYER_INITIAL_STATE } from "./PlayerContext";
import { getTotalElementAndSchoolExp } from "./spells/ElementsAndSchools";
import { getSpellByName } from "./spells/Spells";
import { getTotalSecondsPlayed } from "./timetick/TotalTimePlayed";

const gzipCompress = promisify(zlib.gzip);
const gzipDecompress = promisify(zlib.gunzip);

export async function loadData(): Promise<PlayerContextState> {
  const jsonValue = await AsyncStorage.getItem("PlayerContextState");
  if (jsonValue !== null) {
    const state = JSON.parse(jsonValue);

    // Fix all the save compatibility changes
    return makeCompatible(state);
  }
  return clone(PLAYER_INITIAL_STATE);
}

export async function saveData(state: PlayerContextState): Promise<void> {
  const lastSaveDataTime = Math.max(
    Number.parseFloat(
      (await AsyncStorage.getItem("LastSaveSecondsPlayed")) || "0",
    ),
    0,
  );
  if (Math.max(getTotalSecondsPlayed(state), 0) >= lastSaveDataTime) {
    await saveDataUnsafe(state);
  } else {
    throw new Error("Not saving!!");
  }
}

export async function saveDataUnsafe(state: PlayerContextState): Promise<void> {
  const jsonValue = JSON.stringify(state);
  await AsyncStorage.setItem(
    "LastSaveSecondsPlayed",
    Math.max(getTotalSecondsPlayed(state), 0).toString(),
  );
  await AsyncStorage.setItem("PlayerContextState", jsonValue);
}

export function makeCompatible(obj: PlayerContextState): PlayerContextState {
  if (obj.global.autocastLoadouts == null) {
    obj.global.autocastLoadouts = Object.keys(obj.autocast.loadouts).map(
      (loadoutId) => ({
        name: "",
        allocation: obj.autocast.loadouts[loadoutId],
      }),
    );
  }
  if (obj.global.quickbars == null) {
    obj.global.quickbars = [clone(obj.quickbar)];
  }
  for (let key in obj.autocast.apprenticeAllocation) {
    if (getSpellByName(key) == null) {
      delete obj.autocast.apprenticeAllocation[key];
    }
  }
  for (let loadoutIdx in obj.global.autocastLoadouts) {
    const loadout = obj.global.autocastLoadouts[loadoutIdx]?.allocation;
    if (loadout != null) {
      for (let key in Object.keys(loadout)) {
        if (getSpellByName(key) == null) {
          delete loadout[key];
        }
      }
    }
  }
  for (let quickbarIdx in obj.global.quickbars) {
    const quickbar = obj.global.quickbars[quickbarIdx] ?? [];
    obj.global.quickbars[quickbarIdx] = quickbar.filter(
      (item) => item.type !== "action" || getActionByName(item.name) != null,
    );
  }

  if (obj.global.activeSecondsPlayed == null) {
    obj.global.activeSecondsPlayed = "untracked";
  }
  if (obj.thisRun.activeSecondsPlayed == null) {
    obj.thisRun.activeSecondsPlayed = "untracked";
  }

  obj = grantBestiaryEntriesForOneOff(obj);

  // 1.12: Introduces ever_ global flags
  for (let flag in obj.flags) {
    obj.global.flags["ever_" + flag] = true;
  }
  obj.global.flags["storyline_event_ever_triggered"] = true;
  if (getTotalElementAndSchoolExp(obj) >= 10000) {
    obj.global.flags["ever_retirement_unlocked"] = true;
  }

  // Always unset 8x and 2x speed flags
  obj = clearFlag("time_piece_8x_speedup_enabled")(obj);
  obj = clearFlag("time_piece_speedup_enabled")(obj);

  return deepmerge(clone(PLAYER_INITIAL_STATE), obj);
}

function grantBestiaryEntriesForOneOff(
  obj: PlayerContextState,
): PlayerContextState {
  // Bestiary stuff
  const enemiesToAdd = [];
  if (
    hasGlobalFlagIncludingNextRun(obj, "Conjuration_ultimate_event_completed")
  ) {
    enemiesToAdd.push("ultimateMasterConjurer");
  }
  if (
    hasGlobalFlagIncludingNextRun(obj, "Enchantment_ultimate_event_completed")
  ) {
    enemiesToAdd.push("ultimateMasterEnchanter");
  }
  if (hasGlobalFlagIncludingNextRun(obj, "Illusion_ultimate_event_completed")) {
    enemiesToAdd.push("ultimateMasterIllusionist");
  }
  if (hasGlobalFlagIncludingNextRun(obj, "Alchemy_ultimate_event_completed")) {
    enemiesToAdd.push("ultimateMasterAlchemist");
  }
  if (
    hasGlobalFlagIncludingNextRun(obj, "Evocation_ultimate_event_completed")
  ) {
    enemiesToAdd.push("ultimateMasterEvoker");
  }
  if (
    hasGlobalFlagIncludingNextRun(obj, "Protection_ultimate_event_completed")
  ) {
    enemiesToAdd.push("ultimateMasterProtector");
  }
  if (hasGlobalFlagIncludingNextRun(obj, "Breeding_ultimate_event_completed")) {
    enemiesToAdd.push("ultimateMasterBreeder");
  }
  if (
    hasGlobalFlagIncludingNextRun(obj, "Summoning_ultimate_event_completed")
  ) {
    enemiesToAdd.push("ultimateMasterSummoner");
  }
  if (
    hasGlobalFlagIncludingNextRun(obj, "Divination_ultimate_event_completed")
  ) {
    enemiesToAdd.push("ultimateMasterDiviner");
  }
  if (
    hasGlobalFlagIncludingNextRun(obj, "Conjuration_mastery_event_completed")
  ) {
    enemiesToAdd.push("masterConjurer");
  }
  if (
    hasGlobalFlagIncludingNextRun(obj, "Enchantment_mastery_event_completed")
  ) {
    enemiesToAdd.push("masterEnchanter");
  }
  if (hasGlobalFlagIncludingNextRun(obj, "Illusion_mastery_event_completed")) {
    enemiesToAdd.push("masterIllusionist");
  }
  if (hasGlobalFlagIncludingNextRun(obj, "Alchemy_mastery_event_completed")) {
    enemiesToAdd.push("masterAlchemist");
  }
  if (hasGlobalFlagIncludingNextRun(obj, "Evocation_mastery_event_completed")) {
    enemiesToAdd.push("masterEvoker");
  }
  if (
    hasGlobalFlagIncludingNextRun(obj, "Protection_mastery_event_completed")
  ) {
    enemiesToAdd.push("masterProtector");
  }
  if (hasGlobalFlagIncludingNextRun(obj, "Breeding_mastery_event_completed")) {
    enemiesToAdd.push("masterBreeder");
  }
  if (hasGlobalFlagIncludingNextRun(obj, "Summoning_mastery_event_completed")) {
    enemiesToAdd.push("masterSummoner");
  }
  if (
    hasGlobalFlagIncludingNextRun(obj, "Divination_mastery_event_completed")
  ) {
    enemiesToAdd.push("masterDiviner");
  }
  if (hasGlobalFlagIncludingNextRun(obj, "mimic_defeated")) {
    enemiesToAdd.push("mimic");
  }
  if (hasGlobalFlagIncludingNextRun(obj, "dark_wisp_defeated")) {
    enemiesToAdd.push("darkWisp");
  }
  if (obj.global.timesEnemyDefeated == null) {
    obj.global.timesEnemyDefeated = {};
  }
  enemiesToAdd.forEach((enemyId) => {
    obj.global.timesEnemyDefeated[enemyId] = { player: 1, creatures: 0 };
  });
  return obj;
}

export async function loadBase64Data(
  saveData: string,
): Promise<PlayerContextState | null> {
  const decodedBuffer = Buffer.from(
    Buffer.from(saveData, "base64").toString(),
    "base64",
  );
  const decodedJSON = await gzipDecompress(decodedBuffer);
  const decodedData = JSON.parse(decodedJSON.toString());
  if (decodedData !== null) {
    return makeCompatible(decodedData);
  }
  return null;
}

export async function serializeSaveData(
  state: PlayerContextState,
): Promise<string> {
  const compressedJSON = await gzipCompress(JSON.stringify(state));
  // Double converting to Base64 so it copies well on Android
  return Buffer.from(compressedJSON.toString("base64")).toString("base64");
}
